import { useCallback, useRef, useState } from 'react'
import type { CropOverlayRef } from '@/components/app/editor/tools/crop-overlay'
import type { CropToolbarRef } from '@/components/app/editor/tools/crop-toolbar'
import type { CropZoomSliderRef } from '@/components/app/editor/tools/crop-zoom-slider'
import type { ShapeData } from '@/lib/shapes/types/shape-data'

/**
 * Crop mode state: the shape being cropped, its snapshots for undo/reset, the handles onto the
 * crop overlay/slider/toolbar, and the undo floor. `exitCropMode` is the one exit every other mode
 * and command goes through.
 */
export function useCropState() {
  // Crop mode state — entered from the photo context menu's "Crop Photo" item. Mirrors the
  // node-edit state (`use-node-edit-state`): a single shape gets its own resizable-frame overlay +
  // toolbar instead of the normal selection chrome, while everything else on canvas stays inert.
  const [cropShapeId, setCropShapeId] = useState<string | null>(null)
  const [cropShapeData, setCropShapeData] = useState<ShapeData | null>(null)
  const cropShapeIdRef = useRef<string | null>(null)
  // Captures the shape state at the moment crop mode was entered so Reset can restore it.
  const cropOriginalShapeRef = useRef<ShapeData | null>(null)
  // Captures the shape state at the moment the CURRENT gesture (resize/pan/rotate/zoom) began,
  // for undo — see the doc comment on handleCropFrameChange/handlePhotoPanChange
  // (`use-crop-gesture-handlers`) for why `cropShapeData` itself can't be used as the undo "before"
  // snapshot.
  const cropGestureBeforeRef = useRef<ShapeData | null>(null)
  // Imperative handle onto CropOverlay so Escape can ask it to cancel an in-progress resize
  // drag (see the crop-mode keydown-capture effect in `use-edit-mode-key-capture`).
  const cropOverlayRef = useRef<CropOverlayRef>(null)
  // Same idea for CropZoomSlider's thumb drag — Escape cancels a live zoom-scale drag too.
  const cropZoomSliderRef = useRef<CropZoomSliderRef>(null)
  // Same idea for CropToolbar's grip-handle drag — Escape stops it in place instead of also
  // exiting crop mode.
  const cropToolbarRef = useRef<CropToolbarRef>(null)
  // Shared undo stack's depth as of the moment crop mode was entered — `isCropUndoAtFloor`
  // refuses to undo past this while still in crop mode (see the imperative handle in
  // `use-center-canvas-handle` and the doc comment on `CenterCanvasHandle.isCropUndoAtFloor`).
  // Re-captured on every `cropShapeId` transition (including a switch straight from one cropped
  // shape to another, not just enter/exit) so it always reflects THIS crop session's own starting
  // point.
  const cropUndoFloorRef = useRef<number | null>(null)

  cropShapeIdRef.current = cropShapeId
  const isCropMode = cropShapeId != null

  const exitCropMode = useCallback(() => {
    setCropShapeId(null)
    setCropShapeData(null)
  }, [])

  return {
    cropShapeId,
    setCropShapeId,
    cropShapeData,
    setCropShapeData,
    cropShapeIdRef,
    cropOriginalShapeRef,
    cropGestureBeforeRef,
    cropOverlayRef,
    cropZoomSliderRef,
    cropToolbarRef,
    cropUndoFloorRef,
    isCropMode,
    exitCropMode,
  }
}
